import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import RoleForm from '../components/RoleForm';
import PermissionGuard from '../components/PermissionGuard';
import api from '../services/api';

const Roles = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/api/roles');
      setRoles(response.data.roles || []);
      setError('');
    } catch (error) {
      setError('Failed to load roles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedRole(null);
    setFormOpen(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    try {
      await api.delete(`/api/roles/${roleToDelete.id}`);
      fetchRoles();
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete role');
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case 'Admin':
        return 'error';
      case 'Manager':
        return 'warning';
      case 'Staff':
        return 'info';
      case 'ReadOnly':
        return 'default';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Role Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage user roles and permissions
            </Typography>
          </Box>

          <PermissionGuard permission="users.create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Role
            </Button>
          </PermissionGuard>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {roles.map((role) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={role.id}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: role.is_system ? 2 : 0,
                  borderColor: 'primary.main'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {role.name}
                    </Typography>
                  </Box>

                  {role.is_system && (
                    <Chip
                      label="System Role"
                      size="small"
                      color="primary"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {role.description || 'No description'}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      label={`${(role.permissions || []).length} permissions`}
                      size="small"
                      color={getRoleColor(role.name)}
                    />
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => navigate(`/roles/${role.id}`)}
                  >
                    View Details
                  </Button>

                  <Box>
                    <PermissionGuard permission="users.edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(role)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </PermissionGuard>

                    {!role.is_system && (
                      <PermissionGuard permission="users.delete">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(role)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </PermissionGuard>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {roles.length === 0 && (
          <Alert severity="info">
            No roles found. Create your first role to get started.
          </Alert>
        )}
      </Box>

      {/* Role Form Dialog */}
      <RoleForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedRole(null);
        }}
        onSuccess={fetchRoles}
        role={selectedRole}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{roleToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Roles;
